import { useMemo, useRef } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { unifyRepeatsFromBase, applyPBR, loadTextureMaps, swapMeshUV, calculateUVAwareRepeat } from './textureUtils'
import { useAppStore } from '../../stores/appStore'

function PocketCmp({ pocketUrl, useUvChecker, rotateQuarterTurns, swapUV }: { 
  pocketUrl: string
  useUvChecker: boolean
  rotateQuarterTurns: number
  swapUV: boolean
}) {
  const { bedConfig } = useAppStore()
  const pocketModel = useLoader(FBXLoader, pocketUrl)
  const BASE_SCALE = 0.05
  
  // Use bed textile instead of pocket's own texture
  const texture = bedConfig.textile?.currentTexture || null

  // Get bed bounding box (in bed group local space)
  const bedBox = (window as any).__bed_bbox__

  if (!bedBox || bedBox.isEmpty()) return null as any

  // Store bedBox.max.y in a ref so it stays stable (bedBox object might be modified elsewhere)
  const bedBoxMaxYRef = useRef<number | null>(null)
  if (bedBox && !bedBox.isEmpty() && bedBoxMaxYRef.current === null) {
    bedBoxMaxYRef.current = bedBox.max.y
  }

  // Calculate pocket position - on right side of bed, always on top of bed (not topper)
  const pocketSize = useMemo(() => {
    if (!pocketModel) return null
    const box = new THREE.Box3().setFromObject(pocketModel)
    return new THREE.Vector3().subVectors(box.max, box.min).multiplyScalar(BASE_SCALE)
  }, [pocketModel])
  
  // Position on right side of bed - just outside the bed edge
  const pocketX = bedBox.max.x + (pocketSize ? pocketSize.x * 0.01 : 0) - 0.05 // Small gap from bed edge
  // Position closer to headboard (headboard is at bedBox.min.z)
  // Move pocket about 1/3 of the way from center towards headboard
  const bedLength = bedBox.max.z - bedBox.min.z
  const pocketZ = bedBox.min.z + bedLength * 0.25 // 25% from headboard end (closer to headboard than center)
  
  // Clone and setup pocket
  const clone = useMemo(() => {
    const c = pocketModel.clone()
    c.scale.setScalar(BASE_SCALE)
    
    const pocketBox = new THREE.Box3().setFromObject(pocketModel)
    const pocketCenter = pocketBox.getCenter(new THREE.Vector3())
    const pocketMin = pocketBox.min
    
    c.position.set(-pocketCenter.x * BASE_SCALE, -pocketMin.y * BASE_SCALE, -pocketCenter.z * BASE_SCALE)
    
    // Apply textures if available
    if (texture?.base_color_url || (texture as any)?.base_color_urls) {
      c.traverse((child: any) => {
        if (child.isMesh && child.material) {
          swapMeshUV(child, swapUV, `pocket`)
          const mat = new THREE.MeshStandardMaterial()
          mat.color = new THREE.Color(0xffffff)
          ;(mat as any).emissive?.setRGB?.(0, 0, 0)
          ;(mat as any).vertexColors = false
          const turns: number = Math.abs(rotateQuarterTurns || 0) % 4
          
          const loader = new THREE.TextureLoader()
          const maps = loadTextureMaps(texture, loader)
          
          // Calculate UV-aware repeat based on pocket dimensions
          // Pocket is small, approximate size: ~0.3m x 0.2m
          const pocketSize = new THREE.Vector3().subVectors(pocketBox.max, pocketBox.min)
          const BASE_SCALE = 0.05
          const pocketWidthM = (pocketSize.x / BASE_SCALE) / 100
          const pocketHeightM = (pocketSize.y / BASE_SCALE) / 100
          
          // Calculate repeat values accounting for UV scaling
          const repeat = calculateUVAwareRepeat(child, pocketWidthM || 0.3, pocketHeightM || 0.2, false, true) // Normalize UV span for consistency
          
          // Apply PBR with UV-aware repeat values
          applyPBR({
            mat,
            origMat: child.material,
            type: 'textile',
            meshName: child.name,
            maps,
            rotateTurns: turns,
            hasUV2: swapUV,
            repeat,
            offset: { x: 0, y: 0 },
            rotation: 0
          })
          child.material = mat
          unifyRepeatsFromBase(child.material)
          mat.needsUpdate = true
        }
      })
    }

    c.traverse((child: any) => {
      if (child.isMesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })

    return c
  }, [pocketModel, pocketUrl, texture, swapUV, rotateQuarterTurns])

  // Calculate pocket's bounding box after transformations
  // We need both min and max to align the top with bed top
  const cloneBoxMaxYRef = useRef<number | null>(null)
  if (clone && cloneBoxMaxYRef.current === null) {
    const box = new THREE.Box3().setFromObject(clone)
    cloneBoxMaxYRef.current = box.max.y
  }

  // Position pocket so its top aligns with bed top (NOT topper) in bed group local space
  // Use stable ref values to prevent position changes
  const baseY = bedBoxMaxYRef.current ?? bedBox.max.y
  // Align top of pocket with top of bed: group Y = bed top - pocket top (in local space)
  const pocketY = baseY - (cloneBoxMaxYRef.current ?? 0)

  return (
    <group position={[pocketX, pocketY, pocketZ]} rotation={[0, Math.PI / 2, 0]}>
      <primitive object={clone} />
    </group>
  )
}

export default PocketCmp

